import {AtomContainer, AtomContainerQuery, IAtomModel} from "@alchemiesolns/atomcontainer"
import {ElementHelper} from "@alchemiesolns/elements";
import {isLeftSidePaperLabel} from "./isLeftSidePaperLabel";
import {getReversableGroup} from "./getReversableGroup";

// To calculate the atom symbol we need...
// 1) The atom's symbol
//      This takes visibility and implicit hydrogens into account
// 2) Reversed symbol (e.g. CH3 vs H3C)
//      We need the average bond direction to see if the label is on the left
//      or right side
// 3) Does it use subscripts? (hBonds > 0)
// 4) Where is it anchored
//      The atom has a physical location. If it were 1 letter, it would be at that
//      location. If it's a label, where does the text start and end?

// Calculate the symbols.
// Determine if the symbols need reversing
//  special groups (reversable groups, halogens)
// combine symbols

// These are done somewhere else...
// Determine location
// render text
//  spacing
//  super/subscript
//  anchoring

export const getAtomSymbolWithImplicitHydrogens = (a: IAtomModel, container: AtomContainer, visibleAtoms: IAtomModel[], useSubscripts=false) => {
    const attachedHs = AtomContainerQuery.getImplicitHydrogensAttachedTo(a, container);
    const hiddenHs = attachedHs.filter(a => !visibleAtoms.includes(a));
    let syms = calculateSymbolsWithImplicitHs(a.symbol, hiddenHs.length, useSubscripts);

    const bonds = AtomContainerQuery.getBondsAttachedTo(a, container.bonds);
    const nonImplicitBonds = bonds.filter(b => visibleAtoms.includes(b.a1) && visibleAtoms.includes(b.a2));
    const isLeftSideSymbol = isLeftSidePaperLabel(a, nonImplicitBonds);

    if (isLeftSideSymbol) {
        syms = syms.reverse();

        // Special case: Groups that come in as labels.
        // Everything above this should just leave syms as a 1
        // length array.
        const reversedGroup = getReversableGroup(a.symbol);
        if (reversedGroup) {
            // Reversed spellings (e.g. NO2 → O2N) are plain ASCII; re-apply subscripts
            // so counts match the pre-flip path (NO₂ → O₂N).
            syms = useSubscripts ? convertSymbolToSubscript([reversedGroup]) : [reversedGroup];
        }
    }

    const numGroups = syms.length;
    const symbol = syms.join("");

    return {symbol, numGroups, isLeftSideSymbol};
}

const calculateSymbolsWithImplicitHs = (sym: string, numHBonds: number, useSubscripts=false) => {
    // In standalone everything is e + Hn except for Halogens with single H
    let symbols = [sym];
    // console.log(sym)
    if (numHBonds > 0) {
        const hSym = numHBonds === 1 ? "H" : "H" + numHBonds;
        if (sym === "H") {
            // Special cases for H, H2
            symbols = [hSym]
        } else if (ElementHelper.isHalogen(sym) && numHBonds >= 1) {
            // H before halogen for any collapsed H count (HF, H2F, H2Cl, …) so
            // isLeftSidePaperLabel flips the same way as for H2O / H2N.
            symbols = [hSym, sym];
        } else if ((sym === "O" || sym === "N") && numHBonds >= 1) {
            // H before O/N so left/right flipping matches skeletal convention (H2O/OH2,
            // H2N/NH2, and single-H cases HO/OH, HN/NH). Without H first, an alcohol with the
            // chain to the right of oxygen rendered "OH" while the collapsed H sits on the
            // opposite side of O (paper layout expects HO).
            symbols = [hSym, sym];
        } else if (sym === "Mg" && numHBonds >= 1) {
            // Grignard-style labels read HMg / H2Mg like HBr, not MgH (element+H order).
            symbols = [hSym, sym];
        } else {
            symbols = [sym, hSym];
        }
    }
    // console.log(symbols)
    symbols = useSubscripts ? convertSymbolToSubscript(symbols) : symbols;
    return symbols;
}

const convertSymbolToSubscript = (symbols: string[]) => {
    return symbols.map(symbol => {
        return symbol.replace(/\d/g, (digit) => convertToSubscript(digit));
    });
}

const convertToSubscript = (code: string) => {
    switch (code) {
        case "0":
            return "₀";
        case "1":
            return "₁";
        case "2":
            return "₂";
        case "3":
            return "₃";    
        case "4":
            return "₄";
        case "5":
            return "₅";
        case "6":
            return "₆";
        case "7":
            return "₇";
        case "8":
            return "₈";
        case "9":
            return "₉";
        default:
            return "";
    }
};